import {FEATURE_RESTRICTION_IDS} from 'helpers/constants/restrictions';
import {
  DELIVERY_PROVIDER_TYPE,
  ORDER,
  ORDER_STATUS
} from 'helpers/constants/orders_v2';
import {
  checkIsFutureOrder,
  isEventsOrder
} from 'helpers/functionUtils/ordersUtilsV2';

// A cancelled or completed order keeps its dispatch provider and has no delivery,
// so without this it would still look like it is waiting for a Sauce driver.
const isClosedOrder = (order) =>
  [ORDER_STATUS.CANCELLED, ORDER_STATUS.COMPLETED].includes(
    order?.[ORDER.ORDER_STATUS]
  );

export const hasHybridDeliveryFeature = (serviceFeatures) =>
  !!serviceFeatures?.includes(FEATURE_RESTRICTION_IDS.HYBRID_DELIVERY);

export const HYBRID_HOLD_SECONDS = 210;

// The same window as the Tablet, counted from when the order reached
// us. The backend counts from its own clock and books a little later.
export const hybridHoldExpiresAt = (order) => {
  const receivedAt = order?.[ORDER.RECEIVED_AT];
  if (!receivedAt) return null;

  const deadline = new Date(receivedAt).getTime() + HYBRID_HOLD_SECONDS * 1000;
  return Number.isNaN(deadline) ? null : new Date(deadline).toISOString();
};

export const isHybridHoldRunning = (order, serviceFeatures) => {
  if (!hasHybridDeliveryFeature(serviceFeatures)) return false;
  if (!isEventsOrder(order) || checkIsFutureOrder(order)) return false;
  if (isClosedOrder(order)) return false;

  const delivery = order?.[ORDER.DELIVERY];
  if (delivery?.provider !== DELIVERY_PROVIDER_TYPE.DISPATCH) return false;
  if (delivery?.id) return false;

  const expiresAt = hybridHoldExpiresAt(order);
  return !!expiresAt && new Date(expiresAt).getTime() > Date.now();
};

// Between the countdown hitting zero and the delivery arriving, the order is
// still ours to watch: the backend books a little after the window closes.
export const isHybridBookingPending = (order, serviceFeatures) => {
  if (!hasHybridDeliveryFeature(serviceFeatures)) return false;
  if (!isEventsOrder(order) || checkIsFutureOrder(order)) return false;
  if (isClosedOrder(order)) return false;

  const delivery = order?.[ORDER.DELIVERY];
  if (delivery?.provider !== DELIVERY_PROVIDER_TYPE.DISPATCH) return false;
  if (delivery?.id) return false;

  const expiresAt = hybridHoldExpiresAt(order);
  return !!expiresAt && new Date(expiresAt).getTime() <= Date.now();
};

export const isSelfDeliveryOrder = (order) =>
  order?.[ORDER.DELIVERY]?.provider === DELIVERY_PROVIDER_TYPE.LOCATION;

// While Sauce is still due to book a driver, offering Book a Driver would let
// the merchant end up with two couriers on the same order.
export const isSauceDriverPending = (order, serviceFeatures) => {
  if (!hasHybridDeliveryFeature(serviceFeatures)) return false;
  if (!isEventsOrder(order) || checkIsFutureOrder(order)) return false;
  if (isClosedOrder(order)) return false;

  const delivery = order?.[ORDER.DELIVERY];
  return (
    delivery?.provider === DELIVERY_PROVIDER_TYPE.DISPATCH && !delivery?.id
  );
};
